import { Badge, Box, Button, Card, CardBody, HStack, Icon, Image, Text, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAssistance } from "../../plugin/hooks";
import PhoneOff from "../../plugin/components/common/icons/PhoneOff";
import PhoneOn from "../../plugin/components/common/icons/PhoneOn";
import moment from "moment/min/moment-with-locales";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks";
import { fetchVehicle } from "../../plugin/api/vehicle";
import { OccupantIcon } from "../../plugin/components/common/icons";
import { LuRefreshCw } from "react-icons/lu";
import { keyframes } from "@emotion/react";
import "./AssistanceCard.css"

const animationKeyframes = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const animation = `${animationKeyframes} 2s linear infinite`;

/**
 * Renders an assistance card component
 * 
 * @param {Object} props - The props of the component
 * @returns The assistance card component
 */
const AssistanceCard = ({ assistance, readOnly, onAssistanceSelect, index, onMouseEnter, onMouseLeave, ...props }) => {
    const [image, setImage] = useState(null);
    const { fetchBearer } = useAuth();
    const { assistanceStart, onAssistanceRequestTokenChange } = useAssistance();
    const [assistanceTime, setAssistanceTime] = useState(null);
    const {
        i18n: { language },
    } = useTranslation();
    const queryClient = useQueryClient()

    const locale = useMemo(() => language.slice(0, 2), [language]);

    /**
     * Fetches the vehicle data
     * 
     * @returns The vehicle data
     */
    const vehicleQueryFunction = useCallback(
        async () => {
            const bearer = await fetchBearer()

            return fetchVehicle(assistance?.VIN, bearer)
        },
        [assistance?.VIN, fetchBearer],
    );

    const vehicleQuery = useQuery({
        queryKey: ["vehicle", assistance?.VIN],
        queryFn: vehicleQueryFunction,
        enabled: !!assistance?.VIN,
    });

    const occupants = useMemo(() => {
        const data = vehicleQuery?.data?.Vehicle?.Occupant;
        let count = 0;

        if (assistance?.fake) {
            return assistance?.fake?.occupants;
        }

        if (!data) {
            queryClient.invalidateQueries({ queryKey: ["vehicle", assistance?.VIN] });
            return -1;
        }

        if (data?.Row1?.DriverSide?.IsOccupied) {
            count += 1;
        }
        if (data?.Row1?.Middle?.IsOccupied) {
            count += 1;
        }
        if (data?.Row1?.PassengerSide?.IsOccupied) {
            count += 1;
        }
        if (data?.Row2?.DriverSide?.IsOccupied) {
            count += 1;
        }
        if (data?.Row2?.Middle?.IsOccupied) {
            count += 1;
        }
        if (data?.Row2?.PassengerSide?.IsOccupied) {
            count += 1;
        }

        return count;
    }, [vehicleQuery, assistance, queryClient])

    useEffect(() => {
        const fetchImage = async () => {
            try {
                const response = await import(`../../assets/images/${assistance?.make}_${assistance?.model}.png`);

                setImage(response?.default);
            } catch (error) {
                console.error(error);
            }
        }

        fetchImage();
    }, [assistance?.model, assistance?.make]);

    useEffect(() => {
        if (assistanceStart) {
            const intervalId = setInterval(() => {
                setAssistanceTime(Date.now() - assistanceStart);
            }, 1000);

            return () => {
                setAssistanceTime(0);
                clearInterval(intervalId);
            }
        }
    }, [assistanceStart])

    const timestamp = useMemo(() => parseInt(assistance?.timestamp) || 0, [assistance?.timestamp])

    const assistanceColor = useMemo(() => {
        if (assistance?.category === "ecall") {
            return "red";
        } else if (assistance?.category === "bcall") {
            return "orange";
        } else {
            return "purple";
        }
    }, [assistance?.category])

    const assistanceLabel = useMemo(() => {
        if (assistance?.category === "ecall") {
            return "E-Call";
        } else if (assistance?.category === "bcall") {
            return "B-Call";
        } else {
            return "S-Call";
        }
    }, [assistance?.category])

    return (
        <Box as={motion.div} bgGradient={"linear(#FFFFFF80, #FFFFFF00 46%, #FFFFFF00)"} transition={"1s ease-in-out"} width={!readOnly && "300px"} position={readOnly ? "relative" : "absolute"} left={index * (300 / 3)} borderRadius={"20px"} padding={"1px"} whileHover={!readOnly && { scale: "1.1", opacity: 1, zIndex: 1, boxShadow: "0px 0px 20px #1F9FFF", background: "linear-gradient(#1F9FFF, #1F9FFF00 46%, #1F9FFF00)", filter: "brightness(1)" }} boxShadow={readOnly ? "0px 0px 20px #00000033" : "0px 0px 20px #000000"} filter={!readOnly && "brightness(.7)"} onMouseEnter={() => !readOnly && onMouseEnter(index)} onMouseLeave={() => !readOnly && onMouseLeave(index)} {...props}>
            <Card bgGradient={readOnly ? "linear(#3D4247, #9AA5AE 54%, #FFFFFF)" : "linear(#11202F, #889097 60%, #FFFFFF)"} color={"white"} borderRadius={"20px"}>
                <CardBody>
                    <VStack gap={"20px"}>
                        <HStack justifyContent={"space-between"} width={"full"}>
                            <Badge padding={"3px 12px"} borderRadius={"full"} color={"white"} backgroundColor={assistanceColor} fontWeight={"500"} fontSize={"12px"} textTransform={"lowercase"}>
                                {assistanceLabel}
                            </Badge>
                            <HStack>
                                <Icon as={OccupantIcon} boxSize={"18px"} />
                                {occupants >= 0 ? (
                                    <Text fontSize={"18px"} fontWeight={400}>
                                        {occupants}
                                    </Text>
                                ) : (
                                    <Box as={motion.div} animation={animation} boxSize={"18px"}><Icon as={LuRefreshCw} boxSize={"18px"} /></Box>
                                )}
                            </HStack>
                            <Text fontSize={"12px"} fontWeight={"400"}>
                                {assistance?.driver_name}
                            </Text>
                        </HStack>
                        {readOnly && (
                            <VStack>
                                <Text fontSize={"35px"} fontWeight={300} >{moment(assistanceTime || 0).format("mm : ss")}</Text>
                            </VStack>
                        )}
                        <VStack gap={0}>
                            <Text fontSize={"24px"} fontWeight={"200"} color={"#D9DDDD"}>{assistance?.make}</Text>
                            <Text fontSize={"48px"} fontWeight={"700"} textTransform={"uppercase"} backgroundImage={"linear-gradient(#FFFFFF, #FFFFFF20 50%, #FFFFFF00)"} color={"transparent"} backgroundClip={"text"}>{assistance?.model}</Text>
                            <Image src={image} height={"100px"} />
                        </VStack>
                        {!readOnly && (
                            <Box bgGradient={"linear(#0CC00C, #FFFFFF00)"} padding={"1px"} borderRadius={"full"}>
                                <Button
                                    flexGrow={1}
                                    onClick={() => {
                                        onAssistanceSelect(assistance?.assistance_request_id);
                                    }}
                                    variant={"primary"}
                                    borderWidth={0}
                                    backgroundColor={"white"}
                                    color={"#0CC00C"}
                                    verticalAlign={"baseline"}
                                    boxShadow={"0px 0px 20px #0CC00CAA"}
                                    width={"50px"}
                                    height={"50px"}>
                                    <Icon as={PhoneOn} boxSize={"20px"} />
                                </Button>
                            </Box>
                        )}
                        {readOnly && (
                            <Box bgGradient={"linear(#E1251B, #FFFFFF00)"} padding={"1px"} borderRadius={"full"}>
                                <Button
                                    flexGrow={1}
                                    onClick={() => {
                                        onAssistanceRequestTokenChange({ target: { value: "" } });
                                    }}
                                    variant={"primary"}
                                    borderWidth={0}
                                    backgroundColor={"white"}
                                    color={"#E1251B"}
                                    verticalAlign={"baseline"}
                                    boxShadow={"0px 0px 20px #E1251B"}
                                    width={"50px"}
                                    height={"50px"}>
                                    <Icon as={PhoneOff} boxSize={"25px"} />
                                </Button>
                            </Box>
                        )}
                        <Text color={"#5B5F61"} fontWeight={400}>
                            {moment(timestamp).locale(locale).format("LTS - L")}
                        </Text>
                    </VStack>
                </CardBody>
            </Card>
        </Box>
    )
}

export default AssistanceCard;